/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name covarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/covarmtk}
*/
import covarmtk from './../../../base/ndarray/covarmtk';
setReadOnly( ns, 'covarmtk', covarmtk );

/**
* @name cumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/cumax}
*/
import cumax from './../../../base/ndarray/cumax';
setReadOnly( ns, 'cumax', cumax );

/**
* @name cumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/cumin}
*/
import cumin from './../../../base/ndarray/cumin';
setReadOnly( ns, 'cumin', cumin );

/**
* @name dcovarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dcovarmtk}
*/
import dcovarmtk from './../../../base/ndarray/dcovarmtk';
setReadOnly( ns, 'dcovarmtk', dcovarmtk );

/**
* @name dcumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dcumax}
*/
import dcumax from './../../../base/ndarray/dcumax';
setReadOnly( ns, 'dcumax', dcumax );

/**
* @name dcumaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dcumaxabs}
*/
import dcumaxabs from './../../../base/ndarray/dcumaxabs';
setReadOnly( ns, 'dcumaxabs', dcumaxabs );

/**
* @name dcumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dcumin}
*/
import dcumin from './../../../base/ndarray/dcumin';
setReadOnly( ns, 'dcumin', dcumin );

/**
* @name dcuminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dcuminabs}
*/
import dcuminabs from './../../../base/ndarray/dcuminabs';
setReadOnly( ns, 'dcuminabs', dcuminabs );

/**
* @name dmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmax}
*/
import dmax from './../../../base/ndarray/dmax';
setReadOnly( ns, 'dmax', dmax );

/**
* @name dmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmaxabs}
*/
import dmaxabs from './../../../base/ndarray/dmaxabs';
setReadOnly( ns, 'dmaxabs', dmaxabs );

/**
* @name dmaxabssorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmaxabssorted}
*/
import dmaxabssorted from './../../../base/ndarray/dmaxabssorted';
setReadOnly( ns, 'dmaxabssorted', dmaxabssorted );

/**
* @name dmaxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmaxsorted}
*/
import dmaxsorted from './../../../base/ndarray/dmaxsorted';
setReadOnly( ns, 'dmaxsorted', dmaxsorted );

/**
* @name dmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmean}
*/
import dmean from './../../../base/ndarray/dmean';
setReadOnly( ns, 'dmean', dmean );

/**
* @name dmeankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeankbn}
*/
import dmeankbn from './../../../base/ndarray/dmeankbn';
setReadOnly( ns, 'dmeankbn', dmeankbn );

/**
* @name dmeankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeankbn2}
*/
import dmeankbn2 from './../../../base/ndarray/dmeankbn2';
setReadOnly( ns, 'dmeankbn2', dmeankbn2 );

/**
* @name dmeanli
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanli}
*/
import dmeanli from './../../../base/ndarray/dmeanli';
setReadOnly( ns, 'dmeanli', dmeanli );

/**
* @name dmeanlipw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanlipw}
*/
import dmeanlipw from './../../../base/ndarray/dmeanlipw';
setReadOnly( ns, 'dmeanlipw', dmeanlipw );

/**
* @name dmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanors}
*/
import dmeanors from './../../../base/ndarray/dmeanors';
setReadOnly( ns, 'dmeanors', dmeanors );

/**
* @name dmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanpn}
*/
import dmeanpn from './../../../base/ndarray/dmeanpn';
setReadOnly( ns, 'dmeanpn', dmeanpn );

/**
* @name dmeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanpw}
*/
import dmeanpw from './../../../base/ndarray/dmeanpw';
setReadOnly( ns, 'dmeanpw', dmeanpw );

/**
* @name dmeanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanstdev}
*/
import dmeanstdev from './../../../base/ndarray/dmeanstdev';
setReadOnly( ns, 'dmeanstdev', dmeanstdev );

/**
* @name dmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmeanwd}
*/
import dmeanwd from './../../../base/ndarray/dmeanwd';
setReadOnly( ns, 'dmeanwd', dmeanwd );

/**
* @name dmediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmediansorted}
*/
import dmediansorted from './../../../base/ndarray/dmediansorted';
setReadOnly( ns, 'dmediansorted', dmediansorted );

/**
* @name dmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmidrange}
*/
import dmidrange from './../../../base/ndarray/dmidrange';
setReadOnly( ns, 'dmidrange', dmidrange );

/**
* @name dmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmin}
*/
import dmin from './../../../base/ndarray/dmin';
setReadOnly( ns, 'dmin', dmin );

/**
* @name dminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dminabs}
*/
import dminabs from './../../../base/ndarray/dminabs';
setReadOnly( ns, 'dminabs', dminabs );

/**
* @name dminsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dminsorted}
*/
import dminsorted from './../../../base/ndarray/dminsorted';
setReadOnly( ns, 'dminsorted', dminsorted );

/**
* @name dmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmskmax}
*/
import dmskmax from './../../../base/ndarray/dmskmax';
setReadOnly( ns, 'dmskmax', dmskmax );

/**
* @name dmskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmskmaxabs}
*/
import dmskmaxabs from './../../../base/ndarray/dmskmaxabs';
setReadOnly( ns, 'dmskmaxabs', dmskmaxabs );

/**
* @name dmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmskmin}
*/
import dmskmin from './../../../base/ndarray/dmskmin';
setReadOnly( ns, 'dmskmin', dmskmin );

/**
* @name dmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dmskrange}
*/
import dmskrange from './../../../base/ndarray/dmskrange';
setReadOnly( ns, 'dmskrange', dmskrange );

/**
* @name dnanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmax}
*/
import dnanmax from './../../../base/ndarray/dnanmax';
setReadOnly( ns, 'dnanmax', dnanmax );

/**
* @name dnanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmaxabs}
*/
import dnanmaxabs from './../../../base/ndarray/dnanmaxabs';
setReadOnly( ns, 'dnanmaxabs', dnanmaxabs );

/**
* @name dnanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmean}
*/
import dnanmean from './../../../base/ndarray/dnanmean';
setReadOnly( ns, 'dnanmean', dnanmean );

/**
* @name dnanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmeanors}
*/
import dnanmeanors from './../../../base/ndarray/dnanmeanors';
setReadOnly( ns, 'dnanmeanors', dnanmeanors );

/**
* @name dnanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmeanpn}
*/
import dnanmeanpn from './../../../base/ndarray/dnanmeanpn';
setReadOnly( ns, 'dnanmeanpn', dnanmeanpn );

/**
* @name dnanmeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmeanpw}
*/
import dnanmeanpw from './../../../base/ndarray/dnanmeanpw';
setReadOnly( ns, 'dnanmeanpw', dnanmeanpw );

/**
* @name dnanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmeanwd}
*/
import dnanmeanwd from './../../../base/ndarray/dnanmeanwd';
setReadOnly( ns, 'dnanmeanwd', dnanmeanwd );

/**
* @name dnanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmidrange}
*/
import dnanmidrange from './../../../base/ndarray/dnanmidrange';
setReadOnly( ns, 'dnanmidrange', dnanmidrange );

/**
* @name dnanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmin}
*/
import dnanmin from './../../../base/ndarray/dnanmin';
setReadOnly( ns, 'dnanmin', dnanmin );

/**
* @name dnanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanminabs}
*/
import dnanminabs from './../../../base/ndarray/dnanminabs';
setReadOnly( ns, 'dnanminabs', dnanminabs );

/**
* @name dnanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmskmax}
*/
import dnanmskmax from './../../../base/ndarray/dnanmskmax';
setReadOnly( ns, 'dnanmskmax', dnanmskmax );

/**
* @name dnanmskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmskmaxabs}
*/
import dnanmskmaxabs from './../../../base/ndarray/dnanmskmaxabs';
setReadOnly( ns, 'dnanmskmaxabs', dnanmskmaxabs );

/**
* @name dnanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmskmin}
*/
import dnanmskmin from './../../../base/ndarray/dnanmskmin';
setReadOnly( ns, 'dnanmskmin', dnanmskmin );

/**
* @name dnanmskminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmskminabs}
*/
import dnanmskminabs from './../../../base/ndarray/dnanmskminabs';
setReadOnly( ns, 'dnanmskminabs', dnanmskminabs );

/**
* @name dnanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanmskrange}
*/
import dnanmskrange from './../../../base/ndarray/dnanmskrange';
setReadOnly( ns, 'dnanmskrange', dnanmskrange );

/**
* @name dnanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanrange}
*/
import dnanrange from './../../../base/ndarray/dnanrange';
setReadOnly( ns, 'dnanrange', dnanrange );

/**
* @name dnanrangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanrangeabs}
*/
import dnanrangeabs from './../../../base/ndarray/dnanrangeabs';
setReadOnly( ns, 'dnanrangeabs', dnanrangeabs );

/**
* @name dnanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanstdev}
*/
import dnanstdev from './../../../base/ndarray/dnanstdev';
setReadOnly( ns, 'dnanstdev', dnanstdev );

/**
* @name dnanstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanstdevch}
*/
import dnanstdevch from './../../../base/ndarray/dnanstdevch';
setReadOnly( ns, 'dnanstdevch', dnanstdevch );

/**
* @name dnanstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dnanstdevpn}
*/
import dnanstdevpn from './../../../base/ndarray/dnanstdevpn';
setReadOnly( ns, 'dnanstdevpn', dnanstdevpn );

/**
* @name drange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/drange}
*/
import drange from './../../../base/ndarray/drange';
setReadOnly( ns, 'drange', drange );

/**
* @name drangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/drangeabs}
*/
import drangeabs from './../../../base/ndarray/drangeabs';
setReadOnly( ns, 'drangeabs', drangeabs );

/**
* @name dstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dstdev}
*/
import dstdev from './../../../base/ndarray/dstdev';
setReadOnly( ns, 'dstdev', dstdev );

/**
* @name dstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dstdevch}
*/
import dstdevch from './../../../base/ndarray/dstdevch';
setReadOnly( ns, 'dstdevch', dstdevch );

/**
* @name dstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dstdevpn}
*/
import dstdevpn from './../../../base/ndarray/dstdevpn';
setReadOnly( ns, 'dstdevpn', dstdevpn );

/**
* @name dstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dstdevtk}
*/
import dstdevtk from './../../../base/ndarray/dstdevtk';
setReadOnly( ns, 'dstdevtk', dstdevtk );

/**
* @name dstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dstdevwd}
*/
import dstdevwd from './../../../base/ndarray/dstdevwd';
setReadOnly( ns, 'dstdevwd', dstdevwd );

/**
* @name dstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dstdevyc}
*/
import dstdevyc from './../../../base/ndarray/dstdevyc';
setReadOnly( ns, 'dstdevyc', dstdevyc );

/**
* @name dvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dvariance}
*/
import dvariance from './../../../base/ndarray/dvariance';
setReadOnly( ns, 'dvariance', dvariance );

/**
* @name dztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dztest}
*/
import dztest from './../../../base/ndarray/dztest';
setReadOnly( ns, 'dztest', dztest );

/**
* @name dztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/dztest2}
*/
import dztest2 from './../../../base/ndarray/dztest2';
setReadOnly( ns, 'dztest2', dztest2 );

/**
* @name max
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/max}
*/
import max from './../../../base/ndarray/max';
setReadOnly( ns, 'max', max );

/**
* @name maxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/max-by}
*/
import maxBy from './../../../base/ndarray/max-by';
setReadOnly( ns, 'maxBy', maxBy );

/**
* @name maxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/maxabs}
*/
import maxabs from './../../../base/ndarray/maxabs';
setReadOnly( ns, 'maxabs', maxabs );

/**
* @name maxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/maxsorted}
*/
import maxsorted from './../../../base/ndarray/maxsorted';
setReadOnly( ns, 'maxsorted', maxsorted );

/**
* @name mean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mean}
*/
import mean from './../../../base/ndarray/mean';
setReadOnly( ns, 'mean', mean );

/**
* @name meankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/meankbn}
*/
import meankbn from './../../../base/ndarray/meankbn';
setReadOnly( ns, 'meankbn', meankbn );

/**
* @name meankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/meankbn2}
*/
import meankbn2 from './../../../base/ndarray/meankbn2';
setReadOnly( ns, 'meankbn2', meankbn2 );

/**
* @name meanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/meanors}
*/
import meanors from './../../../base/ndarray/meanors';
setReadOnly( ns, 'meanors', meanors );

/**
* @name meanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/meanpn}
*/
import meanpn from './../../../base/ndarray/meanpn';
setReadOnly( ns, 'meanpn', meanpn );

/**
* @name meanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/meanpw}
*/
import meanpw from './../../../base/ndarray/meanpw';
setReadOnly( ns, 'meanpw', meanpw );

/**
* @name meanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/meanwd}
*/
import meanwd from './../../../base/ndarray/meanwd';
setReadOnly( ns, 'meanwd', meanwd );

/**
* @name mediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mediansorted}
*/
import mediansorted from './../../../base/ndarray/mediansorted';
setReadOnly( ns, 'mediansorted', mediansorted );

/**
* @name midrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/midrange}
*/
import midrange from './../../../base/ndarray/midrange';
setReadOnly( ns, 'midrange', midrange );

/**
* @name midrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/midrange-by}
*/
import midrangeBy from './../../../base/ndarray/midrange-by';
setReadOnly( ns, 'midrangeBy', midrangeBy );

/**
* @name midrangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/midrangeabs}
*/
import midrangeabs from './../../../base/ndarray/midrangeabs';
setReadOnly( ns, 'midrangeabs', midrangeabs );

/**
* @name min
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/min}
*/
import min from './../../../base/ndarray/min';
setReadOnly( ns, 'min', min );

/**
* @name minBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/min-by}
*/
import minBy from './../../../base/ndarray/min-by';
setReadOnly( ns, 'minBy', minBy );

/**
* @name minabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/minabs}
*/
import minabs from './../../../base/ndarray/minabs';
setReadOnly( ns, 'minabs', minabs );

/**
* @name minsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/minsorted}
*/
import minsorted from './../../../base/ndarray/minsorted';
setReadOnly( ns, 'minsorted', minsorted );

/**
* @name mskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mskmax}
*/
import mskmax from './../../../base/ndarray/mskmax';
setReadOnly( ns, 'mskmax', mskmax );

/**
* @name mskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mskmaxabs}
*/
import mskmaxabs from './../../../base/ndarray/mskmaxabs';
setReadOnly( ns, 'mskmaxabs', mskmaxabs );

/**
* @name mskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mskmidrange}
*/
import mskmidrange from './../../../base/ndarray/mskmidrange';
setReadOnly( ns, 'mskmidrange', mskmidrange );

/**
* @name mskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mskmin}
*/
import mskmin from './../../../base/ndarray/mskmin';
setReadOnly( ns, 'mskmin', mskmin );

/**
* @name mskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/mskrange}
*/
import mskrange from './../../../base/ndarray/mskrange';
setReadOnly( ns, 'mskrange', mskrange );

/**
* @name nanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmax}
*/
import nanmax from './../../../base/ndarray/nanmax';
setReadOnly( ns, 'nanmax', nanmax );

/**
* @name nanmaxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmax-by}
*/
import nanmaxBy from './../../../base/ndarray/nanmax-by';
setReadOnly( ns, 'nanmaxBy', nanmaxBy );

/**
* @name nanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmaxabs}
*/
import nanmaxabs from './../../../base/ndarray/nanmaxabs';
setReadOnly( ns, 'nanmaxabs', nanmaxabs );

/**
* @name nanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmean}
*/
import nanmean from './../../../base/ndarray/nanmean';
setReadOnly( ns, 'nanmean', nanmean );

/**
* @name nanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmeanors}
*/
import nanmeanors from './../../../base/ndarray/nanmeanors';
setReadOnly( ns, 'nanmeanors', nanmeanors );

/**
* @name nanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmeanpn}
*/
import nanmeanpn from './../../../base/ndarray/nanmeanpn';
setReadOnly( ns, 'nanmeanpn', nanmeanpn );

/**
* @name nanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmeanwd}
*/
import nanmeanwd from './../../../base/ndarray/nanmeanwd';
setReadOnly( ns, 'nanmeanwd', nanmeanwd );

/**
* @name nanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmidrange}
*/
import nanmidrange from './../../../base/ndarray/nanmidrange';
setReadOnly( ns, 'nanmidrange', nanmidrange );

/**
* @name nanmidrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmidrange-by}
*/
import nanmidrangeBy from './../../../base/ndarray/nanmidrange-by';
setReadOnly( ns, 'nanmidrangeBy', nanmidrangeBy );

/**
* @name nanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmin}
*/
import nanmin from './../../../base/ndarray/nanmin';
setReadOnly( ns, 'nanmin', nanmin );

/**
* @name nanminBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmin-by}
*/
import nanminBy from './../../../base/ndarray/nanmin-by';
setReadOnly( ns, 'nanminBy', nanminBy );

/**
* @name nanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanminabs}
*/
import nanminabs from './../../../base/ndarray/nanminabs';
setReadOnly( ns, 'nanminabs', nanminabs );

/**
* @name nanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmskmax}
*/
import nanmskmax from './../../../base/ndarray/nanmskmax';
setReadOnly( ns, 'nanmskmax', nanmskmax );

/**
* @name nanmskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmskmidrange}
*/
import nanmskmidrange from './../../../base/ndarray/nanmskmidrange';
setReadOnly( ns, 'nanmskmidrange', nanmskmidrange );

/**
* @name nanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmskmin}
*/
import nanmskmin from './../../../base/ndarray/nanmskmin';
setReadOnly( ns, 'nanmskmin', nanmskmin );

/**
* @name nanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanmskrange}
*/
import nanmskrange from './../../../base/ndarray/nanmskrange';
setReadOnly( ns, 'nanmskrange', nanmskrange );

/**
* @name nanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanrange}
*/
import nanrange from './../../../base/ndarray/nanrange';
setReadOnly( ns, 'nanrange', nanrange );

/**
* @name nanrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/nanrange-by}
*/
import nanrangeBy from './../../../base/ndarray/nanrange-by';
setReadOnly( ns, 'nanrangeBy', nanrangeBy );

/**
* @name range
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/range}
*/
import range from './../../../base/ndarray/range';
setReadOnly( ns, 'range', range );

/**
* @name rangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/range-by}
*/
import rangeBy from './../../../base/ndarray/range-by';
setReadOnly( ns, 'rangeBy', rangeBy );

/**
* @name rangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/rangeabs}
*/
import rangeabs from './../../../base/ndarray/rangeabs';
setReadOnly( ns, 'rangeabs', rangeabs );

/**
* @name scovarmtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/scovarmtk}
*/
import scovarmtk from './../../../base/ndarray/scovarmtk';
setReadOnly( ns, 'scovarmtk', scovarmtk );

/**
* @name scumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/scumax}
*/
import scumax from './../../../base/ndarray/scumax';
setReadOnly( ns, 'scumax', scumax );

/**
* @name scumaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/scumaxabs}
*/
import scumaxabs from './../../../base/ndarray/scumaxabs';
setReadOnly( ns, 'scumaxabs', scumaxabs );

/**
* @name scumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/scumin}
*/
import scumin from './../../../base/ndarray/scumin';
setReadOnly( ns, 'scumin', scumin );

/**
* @name scuminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/scuminabs}
*/
import scuminabs from './../../../base/ndarray/scuminabs';
setReadOnly( ns, 'scuminabs', scuminabs );

/**
* @name sdsmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sdsmean}
*/
import sdsmean from './../../../base/ndarray/sdsmean';
setReadOnly( ns, 'sdsmean', sdsmean );

/**
* @name sdsmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sdsmeanors}
*/
import sdsmeanors from './../../../base/ndarray/sdsmeanors';
setReadOnly( ns, 'sdsmeanors', sdsmeanors );

/**
* @name sdsnanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sdsnanmeanors}
*/
import sdsnanmeanors from './../../../base/ndarray/sdsnanmeanors';
setReadOnly( ns, 'sdsnanmeanors', sdsnanmeanors );

/**
* @name smax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smax}
*/
import smax from './../../../base/ndarray/smax';
setReadOnly( ns, 'smax', smax );

/**
* @name smaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smaxabs}
*/
import smaxabs from './../../../base/ndarray/smaxabs';
setReadOnly( ns, 'smaxabs', smaxabs );

/**
* @name smaxabssorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smaxabssorted}
*/
import smaxabssorted from './../../../base/ndarray/smaxabssorted';
setReadOnly( ns, 'smaxabssorted', smaxabssorted );

/**
* @name smaxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smaxsorted}
*/
import smaxsorted from './../../../base/ndarray/smaxsorted';
setReadOnly( ns, 'smaxsorted', smaxsorted );

/**
* @name smean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smean}
*/
import smean from './../../../base/ndarray/smean';
setReadOnly( ns, 'smean', smean );

/**
* @name smeankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeankbn}
*/
import smeankbn from './../../../base/ndarray/smeankbn';
setReadOnly( ns, 'smeankbn', smeankbn );

/**
* @name smeankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeankbn2}
*/
import smeankbn2 from './../../../base/ndarray/smeankbn2';
setReadOnly( ns, 'smeankbn2', smeankbn2 );

/**
* @name smeanli
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeanli}
*/
import smeanli from './../../../base/ndarray/smeanli';
setReadOnly( ns, 'smeanli', smeanli );

/**
* @name smeanlipw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeanlipw}
*/
import smeanlipw from './../../../base/ndarray/smeanlipw';
setReadOnly( ns, 'smeanlipw', smeanlipw );

/**
* @name smeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeanors}
*/
import smeanors from './../../../base/ndarray/smeanors';
setReadOnly( ns, 'smeanors', smeanors );

/**
* @name smeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeanpn}
*/
import smeanpn from './../../../base/ndarray/smeanpn';
setReadOnly( ns, 'smeanpn', smeanpn );

/**
* @name smeanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeanpw}
*/
import smeanpw from './../../../base/ndarray/smeanpw';
setReadOnly( ns, 'smeanpw', smeanpw );

/**
* @name smeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smeanwd}
*/
import smeanwd from './../../../base/ndarray/smeanwd';
setReadOnly( ns, 'smeanwd', smeanwd );

/**
* @name smediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smediansorted}
*/
import smediansorted from './../../../base/ndarray/smediansorted';
setReadOnly( ns, 'smediansorted', smediansorted );

/**
* @name smidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smidrange}
*/
import smidrange from './../../../base/ndarray/smidrange';
setReadOnly( ns, 'smidrange', smidrange );

/**
* @name smin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smin}
*/
import smin from './../../../base/ndarray/smin';
setReadOnly( ns, 'smin', smin );

/**
* @name sminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sminabs}
*/
import sminabs from './../../../base/ndarray/sminabs';
setReadOnly( ns, 'sminabs', sminabs );

/**
* @name sminsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sminsorted}
*/
import sminsorted from './../../../base/ndarray/sminsorted';
setReadOnly( ns, 'sminsorted', sminsorted );

/**
* @name smskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smskmax}
*/
import smskmax from './../../../base/ndarray/smskmax';
setReadOnly( ns, 'smskmax', smskmax );

/**
* @name smskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smskmaxabs}
*/
import smskmaxabs from './../../../base/ndarray/smskmaxabs';
setReadOnly( ns, 'smskmaxabs', smskmaxabs );

/**
* @name smskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smskmidrange}
*/
import smskmidrange from './../../../base/ndarray/smskmidrange';
setReadOnly( ns, 'smskmidrange', smskmidrange );

/**
* @name smskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smskmin}
*/
import smskmin from './../../../base/ndarray/smskmin';
setReadOnly( ns, 'smskmin', smskmin );

/**
* @name smskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/smskrange}
*/
import smskrange from './../../../base/ndarray/smskrange';
setReadOnly( ns, 'smskrange', smskrange );

/**
* @name snanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmax}
*/
import snanmax from './../../../base/ndarray/snanmax';
setReadOnly( ns, 'snanmax', snanmax );

/**
* @name snanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmaxabs}
*/
import snanmaxabs from './../../../base/ndarray/snanmaxabs';
setReadOnly( ns, 'snanmaxabs', snanmaxabs );

/**
* @name snanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmean}
*/
import snanmean from './../../../base/ndarray/snanmean';
setReadOnly( ns, 'snanmean', snanmean );

/**
* @name snanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmeanors}
*/
import snanmeanors from './../../../base/ndarray/snanmeanors';
setReadOnly( ns, 'snanmeanors', snanmeanors );

/**
* @name snanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmeanpn}
*/
import snanmeanpn from './../../../base/ndarray/snanmeanpn';
setReadOnly( ns, 'snanmeanpn', snanmeanpn );

/**
* @name snanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmeanwd}
*/
import snanmeanwd from './../../../base/ndarray/snanmeanwd';
setReadOnly( ns, 'snanmeanwd', snanmeanwd );

/**
* @name snanmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmidrange}
*/
import snanmidrange from './../../../base/ndarray/snanmidrange';
setReadOnly( ns, 'snanmidrange', snanmidrange );

/**
* @name snanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmin}
*/
import snanmin from './../../../base/ndarray/snanmin';
setReadOnly( ns, 'snanmin', snanmin );

/**
* @name snanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanminabs}
*/
import snanminabs from './../../../base/ndarray/snanminabs';
setReadOnly( ns, 'snanminabs', snanminabs );

/**
* @name snanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmskmax}
*/
import snanmskmax from './../../../base/ndarray/snanmskmax';
setReadOnly( ns, 'snanmskmax', snanmskmax );

/**
* @name snanmskmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmskmaxabs}
*/
import snanmskmaxabs from './../../../base/ndarray/snanmskmaxabs';
setReadOnly( ns, 'snanmskmaxabs', snanmskmaxabs );

/**
* @name snanmskmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmskmidrange}
*/
import snanmskmidrange from './../../../base/ndarray/snanmskmidrange';
setReadOnly( ns, 'snanmskmidrange', snanmskmidrange );

/**
* @name snanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmskmin}
*/
import snanmskmin from './../../../base/ndarray/snanmskmin';
setReadOnly( ns, 'snanmskmin', snanmskmin );

/**
* @name snanmskminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmskminabs}
*/
import snanmskminabs from './../../../base/ndarray/snanmskminabs';
setReadOnly( ns, 'snanmskminabs', snanmskminabs );

/**
* @name snanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanmskrange}
*/
import snanmskrange from './../../../base/ndarray/snanmskrange';
setReadOnly( ns, 'snanmskrange', snanmskrange );

/**
* @name snanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/snanrange}
*/
import snanrange from './../../../base/ndarray/snanrange';
setReadOnly( ns, 'snanrange', snanrange );

/**
* @name srange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/srange}
*/
import srange from './../../../base/ndarray/srange';
setReadOnly( ns, 'srange', srange );

/**
* @name srangeabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/srangeabs}
*/
import srangeabs from './../../../base/ndarray/srangeabs';
setReadOnly( ns, 'srangeabs', srangeabs );

/**
* @name sstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sstdev}
*/
import sstdev from './../../../base/ndarray/sstdev';
setReadOnly( ns, 'sstdev', sstdev );

/**
* @name sstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sstdevch}
*/
import sstdevch from './../../../base/ndarray/sstdevch';
setReadOnly( ns, 'sstdevch', sstdevch );

/**
* @name sstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sstdevpn}
*/
import sstdevpn from './../../../base/ndarray/sstdevpn';
setReadOnly( ns, 'sstdevpn', sstdevpn );

/**
* @name sstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sstdevtk}
*/
import sstdevtk from './../../../base/ndarray/sstdevtk';
setReadOnly( ns, 'sstdevtk', sstdevtk );

/**
* @name sstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sstdevwd}
*/
import sstdevwd from './../../../base/ndarray/sstdevwd';
setReadOnly( ns, 'sstdevwd', sstdevwd );

/**
* @name sstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sstdevyc}
*/
import sstdevyc from './../../../base/ndarray/sstdevyc';
setReadOnly( ns, 'sstdevyc', sstdevyc );

/**
* @name stdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/stdev}
*/
import stdev from './../../../base/ndarray/stdev';
setReadOnly( ns, 'stdev', stdev );

/**
* @name stdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/stdevch}
*/
import stdevch from './../../../base/ndarray/stdevch';
setReadOnly( ns, 'stdevch', stdevch );

/**
* @name stdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/stdevpn}
*/
import stdevpn from './../../../base/ndarray/stdevpn';
setReadOnly( ns, 'stdevpn', stdevpn );

/**
* @name stdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/stdevtk}
*/
import stdevtk from './../../../base/ndarray/stdevtk';
setReadOnly( ns, 'stdevtk', stdevtk );

/**
* @name stdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/stdevwd}
*/
import stdevwd from './../../../base/ndarray/stdevwd';
setReadOnly( ns, 'stdevwd', stdevwd );

/**
* @name stdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/stdevyc}
*/
import stdevyc from './../../../base/ndarray/stdevyc';
setReadOnly( ns, 'stdevyc', stdevyc );

/**
* @name svariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/svariance}
*/
import svariance from './../../../base/ndarray/svariance';
setReadOnly( ns, 'svariance', svariance );

/**
* @name svariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/svariancech}
*/
import svariancech from './../../../base/ndarray/svariancech';
setReadOnly( ns, 'svariancech', svariancech );

/**
* @name svariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/svariancepn}
*/
import svariancepn from './../../../base/ndarray/svariancepn';
setReadOnly( ns, 'svariancepn', svariancepn );

/**
* @name svariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/svariancetk}
*/
import svariancetk from './../../../base/ndarray/svariancetk';
setReadOnly( ns, 'svariancetk', svariancetk );

/**
* @name svariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/svariancewd}
*/
import svariancewd from './../../../base/ndarray/svariancewd';
setReadOnly( ns, 'svariancewd', svariancewd );

/**
* @name svarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/svarianceyc}
*/
import svarianceyc from './../../../base/ndarray/svarianceyc';
setReadOnly( ns, 'svarianceyc', svarianceyc );

/**
* @name sztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sztest}
*/
import sztest from './../../../base/ndarray/sztest';
setReadOnly( ns, 'sztest', sztest );

/**
* @name sztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/sztest2}
*/
import sztest2 from './../../../base/ndarray/sztest2';
setReadOnly( ns, 'sztest2', sztest2 );

/**
* @name variance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/variance}
*/
import variance from './../../../base/ndarray/variance';
setReadOnly( ns, 'variance', variance );

/**
* @name variancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/variancech}
*/
import variancech from './../../../base/ndarray/variancech';
setReadOnly( ns, 'variancech', variancech );

/**
* @name variancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/variancepn}
*/
import variancepn from './../../../base/ndarray/variancepn';
setReadOnly( ns, 'variancepn', variancepn );

/**
* @name variancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/variancetk}
*/
import variancetk from './../../../base/ndarray/variancetk';
setReadOnly( ns, 'variancetk', variancetk );

/**
* @name variancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/variancewd}
*/
import variancewd from './../../../base/ndarray/variancewd';
setReadOnly( ns, 'variancewd', variancewd );

/**
* @name varianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/varianceyc}
*/
import varianceyc from './../../../base/ndarray/varianceyc';
setReadOnly( ns, 'varianceyc', varianceyc );

/**
* @name ztest
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/ztest}
*/
import ztest from './../../../base/ndarray/ztest';
setReadOnly( ns, 'ztest', ztest );

/**
* @name ztest2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/ndarray/ztest2}
*/
import ztest2 from './../../../base/ndarray/ztest2';
setReadOnly( ns, 'ztest2', ztest2 );


// EXPORTS //

export default ns;
